import React, { useState } from "react";
import { useTenantRoles, useDeleteTenantRole } from "@/api/tenant.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { MoreHorizontal, Plus, Edit, Trash2 } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { TenantRoleDto } from "@exibra/shared";
import { RoleForm } from "./components/role-form/role-form";
import { useTranslation } from "react-i18next";
import { CardsDataTable } from "@/components/table-old/table";

export const Roles = () => {
    const { t } = useTranslation();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const { data: roles = [], isLoading } = useTenantRoles(selectedTenant?.id);
    const { mutate: deleteRole } = useDeleteTenantRole();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<TenantRoleDto | undefined>();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<TenantRoleDto | undefined>();

    const handleDeleteRole = (role: TenantRoleDto) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteRole = () => {
        if (!selectedTenant || !roleToDelete) return;

        deleteRole({
            tenantId: selectedTenant.id,
            roleId: roleToDelete.id,
        });

        setDeleteDialogOpen(false);
        setRoleToDelete(undefined);
    };

    const cancelDeleteRole = () => {
        setDeleteDialogOpen(false);
        setRoleToDelete(undefined);
    };

    const columns: ColumnDef<TenantRoleDto>[] = [
        {
            accessorKey: "name",
            header: t("users.roles.name"),
            size: 180,
            minSize: 150,
            cell: ({ row }) => row.getValue("name"),
        },
        {
            accessorKey: "createdAt",
            header: t("users.roles.assignedUserCount"),
            size: 120,
            minSize: 100,
            cell: ({ row }) => {
                return row.original.assignedUserCount;
            },
        },
        {
            id: "actions",
            header: "",
            size: 50,
            minSize: 50,
            cell: ({ row }) => {
                const role = row.original;

                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-6 w-6 p-0">
                                    <span className="sr-only">{t("general.openMenu")}</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setEditingRole(role)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    {t("users.roles.edit")}
                                </DropdownMenuItem>
                                {role.assignedUserCount === 0 && (
                                    <DropdownMenuItem
                                        onClick={() => handleDeleteRole(role)}
                                        className="text-destructive"
                                    >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        {t("users.roles.delete.title")}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            <CardsDataTable
                title={t("users.roles.title")}
                description={t("users.roles.description")}
                columns={columns}
                data={roles}
                isLoading={isLoading}
                actions={
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t("users.roles.create")}
                    </Button>
                }
            />

            <RoleForm open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />

            <RoleForm
                open={Boolean(editingRole)}
                onClose={() => setEditingRole(undefined)}
                role={editingRole}
            />

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t("users.roles.delete.title")}</DialogTitle>
                        <DialogDescription>
                            {roleToDelete &&
                                t("users.roles.delete.confirm", { name: roleToDelete.name })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={cancelDeleteRole}>
                            {t("general.cancel")}
                        </Button>
                        <Button variant="destructive" onClick={confirmDeleteRole}>
                            {t("general.delete")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
